import {useMemo} from "react";
import {Line} from "recharts";
import {BaseChart, ChartComponent} from "./BaseChart";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {FieldResult} from "../../../types/domain/fieldResult";
import {useMeasureSystemUtils} from "../../../hooks/measureSystemUtilsHooks";
import {MeasureSystemUtils} from "../../../types/domain/measureSystemUtils";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";

export const EvapotranspirationAppliedWaterCumulativeChart: ChartComponent = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const chartData = useMemo(() => data.map((d) => mapFieldResultsToChartData(d, msUtils)), [data, msUtils]);

    return (
        <BaseChart
            title={t("insights.charts.evapotranspiration_applied_water_cumulative.title")}
            yLabel={msUtils.getRainfallUnits()}
            data={chartData}
            helpPopoverTranslationKey="help.popover.fields.cumulative_values"
        >
            <Line
                type="monotone"
                dataKey="actualEvapotranspirationCumulative"
                name={t("insights.charts.evapotranspiration_applied_water_cumulative.line1_name")}
                unit={msUtils.getRainfallUnits()}
                yAxisId="left"
                stroke="#EA463C"
                fill="#EA463C"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="precipitationCumulative"
                name={t("insights.charts.evapotranspiration_applied_water_cumulative.line2_name")}
                unit={msUtils.getRainfallUnits()}
                yAxisId="left"
                stroke="#00CA4D"
                fill="#00CA4D"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
            <Line
                type="monotone"
                dataKey="irrigationAmountCumulative"
                name={t("insights.charts.evapotranspiration_applied_water_cumulative.line3_name")}
                unit={msUtils.getRainfallUnits()}
                yAxisId="left"
                stroke="#A1AEE3"
                fill="#E2E7F8"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult, msUtils: MeasureSystemUtils): DataProps => ({
    date: dayjs(r.date).toDate(),
    actualEvapotranspirationCumulative: msUtils.getRainfall(r.aggregatedProps.actual_evapotranspiration_cumulative),
    precipitationCumulative: msUtils.getRainfall(r.aggregatedProps.precipitation_cumulative),
    irrigationAmountCumulative: msUtils.getRainfall(r.aggregatedProps.irrigation_amount_cumulative),
});

export interface DataProps extends TimeSeriesDataProps {
    actualEvapotranspirationCumulative: number;
    precipitationCumulative: number;
    irrigationAmountCumulative: number;
}
